import { makeStyles, tokens } from '@coreai/fluentui-react';

/**
 * Shared styles for setting rows inside the modal content area.
 *
 * Pages render as a flat borderless list (see `list` / `listRow` below):
 * rows sit directly on the modal background, separated by 1px hairlines.
 * The legacy `card`-per-row look has been retired across every section.
 */
export const useSettingCardStyles = makeStyles({
  cardLabel: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: 0,
    // Prevent orphans on narrow modal widths — section labels like "Send
    // anonymous usage telemetry" wrap to two lines on small viewports and
    // balance reads better than a 4-word + 1-word split.
    textWrap: 'balance',
  },
  cardHelp: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    margin: 0,
    marginBlockStart: tokens.spacingVerticalXXS,
    textWrap: 'pretty',
    maxWidth: '60ch',
  },
  cardControl: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  cardBody: {
    marginBlockStart: tokens.spacingVerticalM,
  },
  sectionSub: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    margin: 0,
    marginBlockEnd: tokens.spacingVerticalL,
    textWrap: 'pretty',
  },
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingInline: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    marginBlockEnd: tokens.spacingVerticalM,
  },
  dangerZone: {
    marginBlockStart: tokens.spacingVerticalXXL,
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorPaletteRedBorder2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorPaletteRedBackground1,
  },
  dangerTitle: {
    margin: 0,
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  dangerHelp: {
    margin: 0,
    marginBlockStart: tokens.spacingVerticalXS,
    marginBlockEnd: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  resetBtn: {
    minWidth: 'auto',
    width: '28px',
    height: '28px',
  },
  tabular: {
    fontVariantNumeric: 'tabular-nums',
  },
  mono: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
  },
  // Shared helpers for section content that needs structural styling
  loadingWrap: {
    padding: tokens.spacingHorizontalXXL,
    display: 'grid',
    placeItems: 'center',
  },
  errorText: {
    color: tokens.colorStatusDangerForeground1,
  },
  badgeOffset: {
    marginInlineStart: tokens.spacingHorizontalS,
  },
  helpRowMargin: {
    marginBlockStart: tokens.spacingVerticalXS,
  },

  // ─── Borderless list pattern ─────────────────────────────────────────
  //
  // The card-per-row approach turns a settings page into a ladder of
  // identical bordered boxes, which destroys hierarchy and adds a lot of
  // chrome for very little structure. Modern settings UIs (Linear,
  // Vercel, Notion, GitHub) skip the card and use a flat list with
  // hairlines. Rows render directly on the modal background; only the
  // label / help / control shape stays.
  //
  // Compose like so:
  //   <div className={styles.list}>
  //     <div className={styles.listRow}> … </div>
  //     <div className={styles.listRow}> … </div>
  //   </div>
  //
  // For pages with natural subgroups (e.g. Privacy → "Telemetry" /
  // "Crash reports"), wrap each cluster in a `listGroup` and prefix it
  // with a `listGroupLabel`. The first group has no top margin so the
  // page header sits flush above it.
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  listRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'start',
    gap: tokens.spacingHorizontalL,
    paddingBlock: tokens.spacingVerticalL,
    // Hairline between adjacent rows. Using `:not(:first-child)` so
    // callers can add or remove rows without touching first/last
    // bookkeeping. Stroke3 is the lightest neutral border — strong
    // enough to scan, quiet enough to disappear when not looking for it.
    ':not(:first-child)': {
      borderBlockStartWidth: '1px',
      borderBlockStartStyle: 'solid',
      borderBlockStartColor: tokens.colorNeutralStroke3,
    },
  },
  listGroup: {
    display: 'flex',
    flexDirection: 'column',
    ':not(:first-child)': {
      marginBlockStart: tokens.spacingVerticalXXL,
    },
  },
  listGroupLabel: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground4,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    margin: 0,
    marginBlockEnd: tokens.spacingVerticalXS,
  },
  // Fluent v9 Textarea doesn't fill its container by default; the root
  // is inline-block-ish. Force full width so the prompt textarea spans
  // the card the way users expect from any other freeform input.
  textareaFull: {
    width: '100%',
  },
  /**
   * Body slot that escapes the left column of a `listRow` and spans the
   * full grid width. Use when the row's secondary content (a table, a
   * code block, a wide textarea) needs to use the whole row instead of
   * being capped by the label column.
   *
   * Compose alongside `listRow` with `gridColumn: 1 / -1` and a top
   * margin that matches `cardBody`.
   */
  rowBodyWide: {
    gridColumnStart: 1,
    gridColumnEnd: -1,
    marginBlockStart: tokens.spacingVerticalM,
  },
  // Active-tenant badge can carry long names ("LogicApps Auto (Corp Dev …)").
  // Cap and ellipsize so it never pushes the Switch tenant button off-screen;
  // the full name is still discoverable via the Switch tenant dialog.
  tenantBadge: {
    maxWidth: '240px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Right-align shortcut keycaps so the Action column reads as a list and
  // the keys cluster against the right edge of the table.
  shortcutsKeyCell: {
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
});
